import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "../../../../../../../../router/LocaleLink";
import { useFinancialAnalytics } from "../../../../../../../../api/services/financial-integration.hooks";
import { PathGenerators } from "../../../../../../../../router/paths";
import {
  relatedStocksKey,
  type RelatedStock,
} from "../../../../../../../../seo/publicStockFields";
import { normalizeHalalStatus } from "../../../../../../../../utils/halalStatus";
import styles from "./RelatedHalalStocks.module.css";

/**
 * "Other halal stocks in <sector>" (or "Halal alternatives…" on a stock that
 * isn't halal): links to peers by industry and sector. Seeded by the route
 * loader (src/routes/symbol.tsx), so the links are in the server HTML; never
 * fetched from the browser. Renders nothing without data.
 */
export function RelatedHalalStocks({ ticker }: { ticker: string }) {
  const { t } = useTranslation();
  const { data: related } = useQuery<RelatedStock[]>({
    queryKey: relatedStocksKey(ticker),
    queryFn: () => [],
    enabled: false,
    staleTime: Infinity,
  });
  const { data: analytics } = useFinancialAnalytics({ symbol: ticker }, { enabled: false });

  const sector = analytics?.data?.companyProfile?.sector?.trim();
  if (!related?.length || !sector) return null;

  const isHalal = normalizeHalalStatus(analytics?.data?.halal_status) === "halal";

  return (
    <section className={styles.card} aria-labelledby="related-stocks-heading">
      <h2 id="related-stocks-heading" className={styles.heading}>
        {t(isHalal ? "pages.stock.related.heading" : "pages.stock.related.alternativesHeading", {
          sector,
        })}
      </h2>
      <ul className={styles.list}>
        {related.map((stock) => (
          <li key={stock.symbol}>
            <Link to={PathGenerators.SYMBOL(stock.symbol)} className={styles.link}>
              <span className={styles.name}>{stock.companyName || stock.symbol}</span>
              <span className={styles.ticker}>{stock.symbol}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
